import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useFirestoreConnect, isLoaded, isEmpty } from 'react-redux-firebase';
import { useWorker } from '@koale/useworker';
import queryString from 'query-string';

import { useTheme } from '../../ThemeContext';

import { getCountPhotos } from './helpers/getCountPhotos';
import { useLocation, useHistory } from 'react-router-dom';

import {
  Card,
  CardInner,
  Header,
  Content,
  ContentInner,
  Title,
  Text,
  Footer,
  Label,
  OptionButton,
  OptionButtonIcon,
} from './DataSource.style';

const DataSource = ({ data = {}, handleAction = () => {} }) => {
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const intl = useIntl();
  const { profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const statistics = useSelector((state) => state.firestore.data['statistics']);
  const photoCategories = useSelector(
    (state) =>
      state.firestore.ordered[`data_sources/${data.id}/photo_categories`]
  );
  const objectsCategories = useSelector(
    (state) =>
      state.firestore.ordered[`data_sources/${data.id}/objects_categories`]
  );
  const [countPhotosWorker, { status: workerStatus }] = useWorker(
    getCountPhotos
  );
  const [countPhotos, setCountPhotos] = useState(undefined);

  const firestoreConnect = [];
  if (isProfileCompany && data.id) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'data_sources',
          doc: data.id,
          subcollections: [{ collection: 'photo_categories' }],
        },
      ],
      storeAs: `data_sources/${data.id}/photo_categories`,
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'data_sources',
          doc: data.id,
          subcollections: [{ collection: 'objects_categories' }],
        },
      ],
      storeAs: `data_sources/${data.id}/objects_categories`,
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    if (statistics) {
      (async () => {
        if (workerStatus !== 'RUNNING') {
          const result = await countPhotosWorker(statistics, data.id);
          setCountPhotos(result);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statistics]);

  useEffect(() => {
    if (
      location &&
      location.search &&
      isLoaded(photoCategories) &&
      isLoaded(objectsCategories)
    ) {
      const queryParams = queryString.parse(location.search);
      if (queryParams && queryParams.query && queryParams.query === data.id) {
        handleAction({
          type: 'EDIT',
          payload: { ...data, photoCategories, objectsCategories },
        });
        history.replace({
          pathname: '/data-sources',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoCategories, objectsCategories, location]);

  const renderHeader = () => {
    const buttons = [
      {
        theme: theme.button.primary,
        disabled: !isLoaded(photoCategories) && !isLoaded(objectsCategories),
        onClick: () =>
          handleAction({
            type: 'EDIT',
            payload: { ...data, photoCategories, objectsCategories },
          }),
        icon: 'create',
        text: intl.formatMessage({ id: 'DATA_SOURCE.EDIT' }),
      },
      {
        theme: theme.button.danger,
        disabled: undefined,
        onClick: () => handleAction({ type: 'DELETE', payload: data }),
        icon: 'delete_outline',
        text: intl.formatMessage({ id: 'DATA_SOURCE.DELETE' }),
      },
    ];

    return (
      <Header theme={theme.dataSource.header}>
        {buttons.map((button, key) => (
          <OptionButton
            theme={button.theme}
            key={key}
            disabled={button.disabled}
            widthAuto
            noMask
            tiny
            onClick={button.onClick}
          >
            <OptionButtonIcon className='material-icons'>
              {button.icon}
            </OptionButtonIcon>
            {button.text}
          </OptionButton>
        ))}
      </Header>
    );
  };

  const renderContent = () => {
    return (
      <Content>
        <ContentInner>
          {data.name && (
            <Title theme={theme.dataSource.title}>{data.name}</Title>
          )}
          {data.id && <Text theme={theme.dataSource.text}>ID: {data.id}</Text>}
        </ContentInner>
      </Content>
    );
  };

  const renderFooter = () => {
    const countCategories =
      ((photoCategories && photoCategories.length) || 0) +
      ((objectsCategories && objectsCategories.length) || 0);
    const labels = [
      {
        children: (
          <>
            {isLoaded(statistics) || isEmpty(statistics)
              ? `${
                  (countPhotos && countPhotos.length) || 0
                } ${intl.formatMessage({
                  id: 'DATA_SOURCE.PHOTOS',
                })}`
              : intl.formatMessage({ id: 'DATA_SOURCE.LOADING_PHOTOS' })}
          </>
        ),
      },
      {
        children: (
          <>
            {`${countCategories} ${intl.formatMessage({
              id: 'DATA_SOURCE.CATEGORY',
            })}`}
          </>
        ),
      },
    ];

    return (
      <Footer theme={theme.dataSource.footer}>
        {labels.map((label, key) => (
          <Label theme={theme.dataSource.label} key={key}>
            {label.children}
          </Label>
        ))}
      </Footer>
    );
  };

  return (
    <Card theme={theme.content.card.default}>
      <CardInner>
        {renderHeader()}
        {renderContent()}
        {renderFooter()}
      </CardInner>
    </Card>
  );
};

DataSource.propTypes = {
  data: PropTypes.object,
};

export default DataSource;
